import {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import GridIcon from "../../icons/grid.svg?react";
import {NavMenuStyled} from "../NavigationCommon";
import type {TMenuV5} from "../NavigationCommon";
import {IconButtonStyled, IconButtonV5} from "../../interaction/IconButtonV5";
import StarIcon from "../../icons/star.svg?react";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, fontSize, radius, zIndex} from "../../styles/styles";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import {useAppStore} from "../../../../StateManager";
import type {View} from "../../../../data/models/View";
import type {Report} from "../../../../data/models/Report";
import type {IViewFolder} from "../../../../data/models/ViewUtils";
import {useClickOutside} from "../../utils";
import {ViewFind} from "../ViewFind";
import {SearchFieldStyled} from "../../input/search/SearchField.styled";
import {TextInputWrapperStyled} from "../../details/datatypes/TextInputV5";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {Functions} from "../../../../utils/function/Functions";
import type {ViewActionType} from "../../sharing/GlobalSharingPopup";
import {NavigationViewSection} from "./NavigationViewSection";
import {ViewElementStyled, type OnWorkspaceRenameActiveChange, type PrimaryWorkspaceViewType, type WorkspaceViewType} from "./WorkspaceViewCommon";

interface INavigationViewSectionProps {
	readonly isHome: boolean;
	readonly viewCount: number;
	readonly openCreateViewPopup: (type: WorkspaceViewType, viewFolderId: string) => void;
	readonly onViewItemShareClick: (viewItem: View | IViewFolder) => void;
	readonly onGlobalViewActionClick: (view: View | Report, viewActionType: ViewActionType, newName?: string) => void;
	readonly onViewPopupOpenChange: (isOpen: boolean) => void;
	readonly onOpenReportPanel: (report: Report) => void;
	readonly onWorkspaceRenameActiveChange: OnWorkspaceRenameActiveChange;
}

type WorkspaceMenuV5 = TMenuV5 & {
	readonly label: PrimaryWorkspaceViewType;
};

const viewMenu: WorkspaceMenuV5[] = [
	{
		label: "Views",
		icon: GridIcon,
	},
	{
		label: "Favorites",
		icon: StarIcon,
	},
];

const getViewPopoutTop = (isFavourite: boolean): string => (isFavourite ? "290px" : "92px");
const getViewPopoutHeight = (isFavourite: boolean): string => (isFavourite ? "400px" : "800px");

export const ViewSection = (props: INavigationViewSectionProps) => {
	const appState = useAppStore((state) => state.appState);
	const modalContainer = appState.app.modalContainer;
	const {
		viewCount,
		openCreateViewPopup,
		isHome,
		onViewItemShareClick,
		onGlobalViewActionClick,
		onViewPopupOpenChange,
		onOpenReportPanel,
		onWorkspaceRenameActiveChange,
	} = props;
	const viewsRef = useRef<HTMLDivElement>(null);
	const _floatingElement = useRef<HTMLDivElement>(null);
	const iconMenuRef = useRef<HTMLDivElement>(null);
	const [viewSearchString, setViewSearchString] = useState<string>("");
	const [isViewsPopupOpen, setIsViewsPopupOpen] = useState<boolean>(false);
	const [isFavouritePopupOpen, setIsFavouritePopupOpen] = useState<boolean>(false);

	useClickOutside([_floatingElement, iconMenuRef], () => {
		setIsViewsPopupOpen(false);
		setIsFavouritePopupOpen(false);
	});

	const onViewsClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		setIsViewsPopupOpen(!isViewsPopupOpen);
		setIsFavouritePopupOpen(false);
		setViewSearchString("");
	};

	const onFavouriteClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		setIsViewsPopupOpen(false);
		setIsFavouritePopupOpen(!isFavouritePopupOpen);
		setViewSearchString("");
	};

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === KeyboardListener.KEY_ESCAPE) {
				setIsViewsPopupOpen(false);
				setIsFavouritePopupOpen(false);
			}
		};

		KeyboardListener.getInstance().signals.down.add(onKeyDown);

		return () => {
			KeyboardListener.getInstance().signals.down.remove(onKeyDown);
		};
	}, []);

	return (
		<>
			<NavigationViewStyled ref={iconMenuRef}>
				{viewMenu.map((menu, i) => {
					return (
						<ViewIconStyled
							key={i}
							$label={menu.label}
							$isActive={(menu.label === "Views" && isViewsPopupOpen) || (menu.label === "Favorites" && isFavouritePopupOpen)}
							onClick={Functions.stopPropagation}
						>
							<IconButtonV5
								onClick={(e) => {
									if (menu.label === "Views") {
										onViewsClick(e);
									} else {
										onFavouriteClick(e);
									}
								}}
								IconComponent={menu.icon}
								isActive={(menu.label === "Views" && isViewsPopupOpen) || (menu.label === "Favorites" && isFavouritePopupOpen)}
								key={menu.label}
								className={menu.label}
								divRef={viewsRef}
							/>
						</ViewIconStyled>
					);
				})}
			</NavigationViewStyled>
			{(isViewsPopupOpen || isFavouritePopupOpen) && (
				<DomPortal destination={modalContainer}>
					<ViewPopoutStyled
						ref={_floatingElement}
						$isFavourite={isFavouritePopupOpen}
					>
						<ViewFind
							key={isViewsPopupOpen ? "views" : "favorites"}
							viewSearchString={viewSearchString}
							setViewSearchString={setViewSearchString}
							autoFocus={viewCount > 0}
						/>
						<NavigationViewSection
							openCreateViewPopup={openCreateViewPopup}
							onlyIcon={false}
							isHome={isHome}
							onViewItemShareClick={onViewItemShareClick}
							onGlobalViewActionClick={onGlobalViewActionClick}
							onViewPopupOpenChange={onViewPopupOpenChange}
							onOpenReportPanel={onOpenReportPanel}
							onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
							searchString={viewSearchString}
							isFavouriteClicked={isFavouritePopupOpen}
							viewCount={viewCount}
						/>
					</ViewPopoutStyled>
				</DomPortal>
			)}
		</>
	);
};

const ViewPopoutStyled = styled.div<{$isFavourite: boolean}>`
	position: absolute;
	left: 48px;
	top: ${(props) => getViewPopoutTop(props.$isFavourite)};
	width: 336px;
	height: ${(props) => getViewPopoutHeight(props.$isFavourite)};
	max-height: calc(100vh - ${(props) => getViewPopoutTop(props.$isFavourite)} - ${baseDistance.md});
	background-color: ${colorPalette.blueGray.c100};
	padding: ${baseDistance.sm};
	box-sizing: border-box;
	overflow: hidden;
	z-index: ${zIndex.secondaryNavigation};
	border-radius: ${radius.md};
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};

	${SearchFieldStyled}, ${NavMenuStyled} {
		width: 320px;
	}

	${SearchFieldStyled} {
		flex: 0 0 auto;
	}

	${NavMenuStyled} {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
	}

	${ViewElementStyled} > ${TextInputWrapperStyled} {
		width: 200px;
	}

	${NavMenuStyled}::-webkit-scrollbar {
		display: none;
	}
`;

const ViewIconStyled = styled.div<{$label: string; $isActive?: boolean}>`
	position: relative;

	${IconButtonStyled} {
		&:hover {
			background-color: ${(props) => (!props.$isActive ? colorPalette.blueGray.c200Light : "")};
		}
	}

	&:hover:after {
		content: "${(props) => props.$label || ""}";
		position: absolute;
		background-color: ${(props) => (props.$isActive ? colorPalette.primary.c500Primary : colorPalette.blueGray.c200Light)};
		color: ${(props) => (props.$isActive ? colorPalette.white : colorPalette.gray.c950)};
		left: 41px;
		top: -4px;
		border-radius: ${radius.sm};
		height: 32px;
		padding: 0 ${baseDistance.sm};
		width: max-content;
		font-size: ${fontSize.md};
		display: flex;
		align-items: center;
	}
`;

const NavigationViewStyled = styled.div`
	display: flex;
	flex-wrap: nowrap;
	flex-direction: column;

	${IconButtonStyled} {
		width: 24px;
		height: 24px;
		min-width: 24px;
	}

	svg {
		width: 16px;
		height: 16px;
	}

	.Favorites {
		svg {
			fill: #f5d254;

			path {
				stroke: #f5d254;
			}
		}
	}
`;
